if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this._initialized = false;
        this.init();

        window.addEventListener('pageshow', (e) => {
          if (e.persisted || document.readyState === 'complete') {
            if (this._initialized) {
              Promise.resolve().then(() => this.updateNavButtons && this.updateNavButtons());
            } else {
              this.init();
            }
          }
        });
      }

      connectedCallback() {
        this.init();
      }

      init() {
        if (this._initialized) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements?.viewer || !this.elements?.thumbnails) return;

        // Preferencia del eje
        this.thumbList = this.elements.thumbnails.querySelector('.slider');
        this.axisPref =
          (this.elements.thumbnails.dataset.axis || '').toLowerCase() ||
          (this.thumbList?.classList.contains('is-vertical') ? 'vertical' : '');

        // Botones
        this.btnPrev = this.elements.thumbnails.querySelector('.slider-button--prev');
        this.btnNext = this.elements.thumbnails.querySelector('.slider-button--next');

        // Calcular desplazamiento
        const getStep = (btn) => parseInt(btn?.dataset.step || '1', 10);
        const getItemSize = () => {
          const item = this.thumbList?.querySelector('.slider__slide');
          if (!item) return 0;
          const cs = getComputedStyle(this.thumbList);
          const gap = parseFloat(cs.rowGap || cs.columnGap || '0') || 0;
          const rect = item.getBoundingClientRect();
          return this.isVertical ? rect.height + gap : rect.width + gap;
        };

        this.scrollByStep = (dir) => {
          const container = this.thumbList || this.elements.thumbnails.slider;
          if (!container) return;
          const delta = getItemSize() * getStep(dir > 0 ? this.btnNext : this.btnPrev);
          const opts = this.isVertical
            ? { top: dir * delta, left: 0 }
            : { left: dir * delta, top: 0 };
          container.scrollBy({ ...opts, behavior: 'smooth' });
          Promise.resolve().then(() => this.updateNavButtons());
        };

        this.btnPrev?.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollByStep(-1);
        });
        this.btnNext?.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollByStep(+1);
        });

        // Ajustar eje
        this.setAxis = () => {
          const verticalWanted = this.axisPref === 'vertical';
          const shouldBeVertical = this.mql.matches && verticalWanted;
          this.isVertical = !!shouldBeVertical;

          if (this.thumbList) {
            this.thumbList.classList.toggle('is-vertical', this.isVertical);
            this.thumbList.classList.toggle('is-horizontal', !this.isVertical);
            this.thumbList.style.overflowY = this.isVertical ? 'auto' : '';
            this.thumbList.style.overflowX = this.isVertical ? '' : 'auto';
          }

          requestAnimationFrame(() => this.updateNavButtons && this.updateNavButtons());
        };

        this.setAxis();
        this.mql.addEventListener('change', () => this.setAxis());

        // Eventos del viewer
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          btn &&
            btn.addEventListener(
              'click',
              this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
            );
        });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        // Recalcular estado inicial de flechas
        const container = this.thumbList || this.elements.thumbnails.slider;
        if (container) {
          container.addEventListener('scroll', () => this.updateNavButtons(), { passive: true });
          window.addEventListener('resize', () => this.updateNavButtons(), { passive: true });

          const imgs = container.querySelectorAll('img');
          let pending = imgs.length;
          if (pending === 0) {
            this.updateNavButtons();
          } else {
            imgs.forEach((img) => {
              if (img.complete) {
                if (--pending === 0) this.updateNavButtons();
              } else {
                img.addEventListener(
                  'load',
                  () => {
                    if (--pending === 0) this.updateNavButtons();
                  },
                  { once: true }
                );
              }
            });
          }

          this.btnPrev?.removeAttribute('disabled');
          this.btnNext?.removeAttribute('disabled');
          requestAnimationFrame(() => this.updateNavButtons());
        }

        this._initialized = true;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) =>
          el.classList.remove('is-active')
        );
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);
        }

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((el) => el.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // Horizontal (móvil)
        if (!this.isVertical) {
          if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
          this.elements.thumbnails.slider?.scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth',
          });
          return;
        }

        // Vertical (desktop)
        const container = this.thumbList || this.elements.thumbnails.slider;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();
        const above = thumbRect.top < containerRect.top + 10;
        const below = thumbRect.bottom > containerRect.bottom - 10;

        if (above || below) {
          container.scrollTo({
            top: thumbnail.offsetTop,
            left: 0,
            behavior: 'smooth',
          });
        }
      }

      updateNavButtons() {
        const container = this.thumbList || this.elements?.thumbnails?.slider;
        if (!container) return;

        const max = this.isVertical
          ? container.scrollHeight - container.clientHeight
          : container.scrollWidth - container.clientWidth;

        const pos = this.isVertical ? container.scrollTop : container.scrollLeft;
        const atStart = pos <= 1;
        const atEnd = pos >= max - 1;
        const noOverflow = max <= 0;

        this.btnPrev?.toggleAttribute('disabled', noOverflow || atStart);
        this.btnNext?.toggleAttribute('disabled', noOverflow || atEnd);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}
